import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";

const values = [
  { icon: "ri-file-list-3-line", title: "真实收录", desc: "不夸大、不虚构，只把真正值得阅读的作品推荐给你，让收录范围有据可查。" },
  { icon: "ri-compass-3-line", title: "清晰分区", desc: "用校园、职场、奇幻、恋爱、剧情等清晰的题材分类，降低每一部好作品的寻找成本。" },
  { icon: "ri-line-chart-line", title: "客观榜单", desc: "以综合数据为依托，热度与口碑并重，让榜单成为可靠的阅读参考而非噱头。" },
  { icon: "ri-user-heart-line", title: "读者为先", desc: "我们始终站在读者的角度，用更友好、更简洁的方式呈现每一部作品。" },
];

const voices = [
  { name: "星野酱", tag: "奇幻爱好者", text: "在这里意外发现了《异界咖啡馆》，单元剧式的治愈故事让我连追三天，分类做得很清楚。" },
  { name: "阿澜", tag: "校园漫迷", text: "实时榜单真的很方便，不用自己到处找冷门好番，周榜推荐基本都很对我口味。" },
  { name: "木木", tag: "恋爱漫读者", text: "恋爱分类更新很及时，评分也靠谱，跟着高分榜单追基本不踩雷。" },
  { name: "一鹿", tag: "剧情党", text: "汉江边的老书店这种文艺向作品都能找到，说明这里的内容筛选确实用心。" },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 羞羞漫画韩国",
    description:
      "了解羞羞漫画韩国的品牌介绍、网站定位、发展理念与内容原则。我们专注整理和推荐优质韩国漫画，让校园、职场、奇幻、恋爱、剧情等题材的好作品被更多人看见。",
  });

  return (
    <main className="w-full bg-background-50">
      <section className="border-b border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <h1 className="mt-4 font-display text-3xl font-black text-foreground-950 md:text-4xl">关于我们</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            为每一位漫友，做一扇通往韩国漫画世界的门。
          </p>
        </div>
      </section>

      {/* 品牌介绍 */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=artistic%20illustration%20of%20a%20sunlit%20workspace%20filled%20with%20sketchbooks%20and%20comic%20storyboards%2C%20warm%20coral%20cream%20and%20amber%20palette%2C%20creative%20storytelling%20atmosphere%2C%20stylized%20modern%20illustration&width=1200&height=800&seq=about-brand-01&orientation=landscape"
              alt="充满漫画分镜与素描本的创意工作台插画"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <p className="font-label text-sm tracking-widest text-primary-600">品牌介绍</p>
            <h2 className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
              从一部好故事开始
            </h2>
            <p className="mt-5 text-sm leading-7 text-foreground-700">
              羞羞漫画韩国诞生于一个简单的念头：好的韩国漫画不该被埋没在海量的信息里。我们整理校园、职场、奇幻、恋爱、剧情等热门题材，也关注那些冷门却动人的佳作，用清晰的分区和客观的榜单，把选择权交还给每一位读者。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              这里不是一个普通的漫画目录，而是一个面向漫友的内容推荐社区。我们相信，阅读本应是一件轻松愉快的事，所以我们把「找漫画」这件事做到最简单。
            </p>
          </div>
        </div>
      </section>

      {/* 网站定位与理念 */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <p className="text-center font-label text-sm tracking-widest text-primary-600">定位与理念</p>
          <h2 className="mt-2 text-center font-display text-2xl font-black text-foreground-950 md:text-3xl">
            我们为什么存在
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-xl bg-background-50 p-5 ring-1 ring-background-200/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-600">
                  <i className={v.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-display text-base font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { icon: "ri-eye-line", title: "内容原则", desc: "收录内容客观真实，评分与热度有据可依，不夸大宣传。" },
              { icon: "ri-heart-3-line", title: "版权意识", desc: "我们仅做内容介绍与推荐，阅读请务必前往正规版权平台。" },
              { icon: "ri-rocket-2-line", title: "持续成长", desc: "收集读者反馈，不断扩充题材与作品，让内容库越来越丰富。" },
            ].map((f) => (
              <div key={f.title} className="flex items-start gap-3 rounded-xl bg-background-50 p-5 ring-1 ring-background-200/70">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-700">
                  <i className={f.icon} aria-hidden="true"></i>
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950">{f.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <p className="text-center font-label text-sm tracking-widest text-accent-600">读者口碑</p>
        <h2 className="mt-2 text-center font-display text-2xl font-black text-foreground-950 md:text-3xl">
          他们这样说
        </h2>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {voices.map((v) => (
            <figure key={v.name} className="flex flex-col rounded-xl bg-background-100 p-5">
              <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true"></i>
              <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">{v.text}</blockquote>
              <figcaption className="mt-5 flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-sm font-bold text-secondary-900">
                  {v.name.charAt(0)}
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{v.name}</p>
                  <p className="text-xs text-foreground-500">{v.tag}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 联系 */}
      <section className="mx-auto max-w-7xl px-4 pb-6 md:px-6">
        <div className="flex flex-col items-start justify-between gap-5 rounded-2xl bg-primary-500 px-6 py-10 md:flex-row md:items-center md:px-10">
          <div>
            <h2 className="font-display text-2xl font-black text-background-50 md:text-3xl">
              有喜欢的漫画想推荐？
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-100">
              无论是投稿推荐、内容合作还是意见反馈，我们都欢迎。和我们一起，让更多好作品被看见。
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-medium text-primary-700 transition-colors hover:bg-background-100"
          >
            联系我们 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </section>

      {/* AboutPage schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于羞羞漫画韩国",
            description: "羞羞漫画韩国的品牌介绍、网站定位、发展理念与内容原则。",
            inLanguage: "zh-CN",
            url: "https://koreope.github.io/about",
          }),
        }}
      />
    </main>
  );
}